import async from 'async';
import React from 'react';
import pluralize from 'pluralize';
import { Link } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import { deleteCampaign } from '../../actions/campaignActions';
import { deletePlaylist } from '../../actions/playlistActions';
import { deleteSequence } from '../../actions/sequenceActions';
import { deleteAsset } from '../../actions/assetActions';
import { deleteSign } from '../../actions/signActions';
import { deleteSchedule } from '../../actions/scheduleActions';
import { deleteFolder } from '../../actions/folderActions';

/**
 * @typedef {Object} ContentItem
 * @property {string|number} [id] - UI or API identifier for the item.
 * @property {string|number} [content_id] - Numeric content identifier used by most content APIs.
 * @property {string} [fileId] - Asset identifier used by file-based content types.
 * @property {'folder'|'image'|'video'|'file'|'playlist'|'sign'|'sequence'|'schedule'|'campaign'|string} type - Content category used to resolve actions.
 * @property {string} name - Display name for the item.
 * @property {string} [parent] - Parent folder path or parent identifier, depending on context.
 * @property {string[]} [tags] - Tags associated with the item.
 * @property {string[]} [usedInSigns] - Sign usage markers returned by the API.
 * @property {string[]} [usedInPlaylists] - Playlist usage markers returned by the API.
 */

/**
 * @typedef {Object.<number, ContentItem>} SelectedContentMap
 */

/**
 * @typedef {Object} DeletionFilterResult
 * @property {ContentItem[]} itemsToDelete - Items that are safe to delete immediately.
 * @property {ContentItem[]} foldersInUse - Folder items blocked by sign usage.
 * @property {ContentItem[]} imagesInUse - Image items blocked by sign usage.
 * @property {ContentItem[]} playlistsInUse - Playlist items blocked by sign usage.
 * @property {Array<{ campaignId: string|number, campaignName: string }>} campaignsInUse - Campaigns blocked by schedule usage.
 * @property {ContentItem[]} videosInUse - Video items blocked by playlist usage.
 */

const window = globalThis.window;
const deleteMap = {
  image: item => deleteAsset(item.fileId),
  video: item => deleteAsset(item.fileId),
  file: item => deleteAsset(item.fileId),
  playlist: item => deletePlaylist(item.content_id),
  sign: item => deleteSign(item.content_id),
  sequence: item => deleteSequence(item.content_id),
  schedule: item => deleteSchedule(item.content_id),
  campaign: item => deleteCampaign(item.content_id),
  folder: item => deleteFolder(item.content_id, item.parent),
};

/**
 * Resolves and starts the correct delete action for a single content item.
 * @param {ContentItem} item - The item to delete.
 * @returns {Promise<*>|null} The delete request promise for supported item types, or `null` when no delete action exists.
 */
export const deleteItem = item => deleteMap[item.type]?.(item) || null;

/**
 * Deletes a list of content items sequentially and refreshes the content data afterwards.
 * @param {Object} params - Helper configuration.
 * @param {(query: string) => *} params.loadFolders - Reloads the folder listing after deletion completes.
 * @param {(query: string) => *} params.loadContentItems - Reloads the visible content list after deletion completes.
 * @param {() => *} params.loadAllContentItems - Reloads the full content list used by other actions.
 * @param {() => *} params.contentItemsUnselectAll - Clears any global content selection state.
 * @param {string} params.search - Current content query string.
 * @param {(notification: { type: string, text: string }) => void} params.addNotification - Displays success or failure notifications.
 * @param {ContentItem[]} params.contentItems - Items to delete in order.
 * @param {(value: boolean) => void} params.setDeepDelete - Resets the deep delete checkbox state after completion.
 * @returns {Promise<void>} Resolves once deletion, notifications, and refresh callbacks have finished.
 */
export const deleteItemsHelper = ({
  loadFolders,
  loadContentItems,
  loadAllContentItems,
  contentItemsUnselectAll,
  search,
  addNotification,
  contentItems,
  setDeepDelete,
}) => {
  return new Promise(resolve => {
    let deleteMessage = '';
    let failureMessage = '';

    async.eachSeries(
      contentItems,
      (contentItem, callback) => {
        deleteMessage = `${contentItem.type}: ${contentItem.name} has been deleted.`;
        failureMessage = `${contentItem.type}: ${contentItem.name} could not be deleted.`;
        deleteItem(contentItem).then(
          res => {
            addNotification({ type: 'success', text: deleteMessage });
            callback(null, res);
          },
          () => {
            addNotification({ type: 'danger', text: failureMessage });
            callback(null, false);
          },
        );
      },
      () => {
        loadFolders('?parent=/&sort=createdDate+desc&count=1000');
        loadContentItems(search);
        loadAllContentItems();
        contentItemsUnselectAll();
        resolve();
        setDeepDelete(false);
      },
    );
  });
};

/**
 * Expands a selection into the concrete list of items that should be deleted.
 * @param {Object} params - Selection and content sources.
 * @param {{ content_items: ContentItem[] }} params.allContentItems - Full content collection fetched for delete resolution.
 * @param {ContentItem[]} params.selectedItems - Explicitly selected items to delete.
 * @param {boolean} params.deleteAllContent - Whether folder contents should also be deleted.
 * @param {{ content_items: ContentItem[] }} params.contentItems - Current visible content collection used for lookup by id.
 * @returns {ContentItem[]} The flattened list of content items that should be processed for deletion.
 */
export const mGetItemsForDeletionHelper = ({
  allContentItems,
  selectedItems,
  deleteAllContent,
  contentItems,
}) => {
  const items = [];
  const contentItemsMap = {};

  contentItems.content_items.forEach(el => {
    // content_id is not guaranteed to be unique across all types, so we need to bucket by type first
    if (!contentItemsMap[el.type]) {
      contentItemsMap[el.type] = {};
    }

    contentItemsMap[el.type][el.content_id] = el;
  });

  selectedItems.forEach(selectedItem => {
    if (deleteAllContent && selectedItem.type === 'folder') {
      const folderPath = `${selectedItem.parent + selectedItem.name}/`;

      allContentItems.content_items.forEach(item => {
        if (item.parent.startsWith(folderPath)) {
          items.push(item);
        }
      });
    }

    items.push(contentItemsMap[selectedItem.type][selectedItem.content_id]);
  });

  return items;
};

/**
 * Configures the delete confirmation modal and wires the delete action callback.
 * @param {Object} params - Delete modal configuration.
 * @param {string} params.title - Modal title text.
 * @param {string} params.subtitle - Modal subtitle text.
 * @param {ContentItem} params.content - Item targeted by the delete action.
 * @param {(title: string) => void} params.setConfirmModalTitle - Updates the modal title.
 * @param {(subtitle: string) => void} params.setConfirmModalSubtitle - Updates the modal subtitle.
 * @param {(handler: Function) => void} params.setConfirmModalOnClick - Stores the modal submit callback.
 * @param {(query: string) => Promise<{ content_items: ContentItem[] }>} params.fetchContentItems - Fetches the complete content set for delete resolution.
 * @param {(value: boolean) => void} params.setDeleteFolderComponent - Toggles the folder-specific delete UI.
 * @param {(label: string) => void} params.setConfirmModalBtnText - Updates the modal button label.
 * @param {(value: boolean) => void} params.setShowConfirmModal - Opens the confirmation modal.
 * @param {(allContentItems: { content_items: ContentItem[] }, selectedItems: ContentItem[], deleteAllContent: boolean) => ContentItem[]} params.mGetItemsForDeletion - Expands the target item into all items that should be deleted.
 * @param {(items: ContentItem[]) => DeletionFilterResult} params.getFilteredItemsForDeletion - Filters out items that cannot be deleted.
 * @param {(items: DeletionFilterResult) => void} params.showFailedDeleteWarning - Shows warnings for items blocked from deletion.
 * @param {(items: ContentItem[]) => void} params.deleteItems - Starts the actual delete requests.
 * @returns {void}
 */
export const prepareDeleteFunctionHelper = ({
  title,
  subtitle,
  content,
  setConfirmModalTitle,
  setConfirmModalSubtitle,
  setConfirmModalOnClick,
  fetchContentItems,
  setDeleteFolderComponent,
  setConfirmModalBtnText,
  setShowConfirmModal,
  mGetItemsForDeletion,
  getFilteredItemsForDeletion,
  showFailedDeleteWarning,
  deleteItems,
}) => {
  setConfirmModalTitle(title);
  setConfirmModalSubtitle(subtitle);
  setConfirmModalOnClick(() => deleteAllContent => {
    fetchContentItems('?count=100000000').then(res => {
      const itemsForDeletion = mGetItemsForDeletion(res, [content], deleteAllContent);
      const items = getFilteredItemsForDeletion(itemsForDeletion);

      showFailedDeleteWarning(items);
      deleteItems(items.itemsToDelete);
    });
  });

  if (content.type === 'folder') {
    setDeleteFolderComponent(true);
  }

  setConfirmModalBtnText('Delete Item');
  setShowConfirmModal(true);
};

/**
 * Splits deletion candidates into deletable items and items that are still in use.
 * @param {Object} params - Content and dependency collections used for deletion checks.
 * @param {ContentItem[]} params.items - Candidate items for deletion.
 * @param {{ folders: { folders: ContentItem[] } }} params.folders - Folder state used to resolve folder usage.
 * @param {{ assets: { assets: ContentItem[] } }} params.assets - Asset state used to resolve image and video usage.
 * @param {{ playlists: { playlists: ContentItem[] } }} params.playlists - Playlist state used to resolve playlist usage.
 * @param {{ campaignSchedule: { campaignScheduleEntries: Array<{ campaignId: string|number, campaignName: string }> } }} params.campaignSchedule - Campaign schedule state used to find campaign usage.
 * @returns {DeletionFilterResult} Buckets describing which items can and cannot be deleted.
 */
export const getFilteredItemsForDeletionHelper = ({
  items,
  folders,
  assets,
  playlists,
  campaignSchedule,
}) => {
  const results = {
    itemsToDelete: [],
    foldersInUse: [],
    imagesInUse: [],
    playlistsInUse: [],
    campaignsInUse: [],
    videosInUse: [],
  };
  const typeMap = {
    folder: {
      store: folders.folders.folders,
      list: results.foldersInUse,
      idKey: 'content_id',
      check: item => Boolean(item.usedInSigns[0]),
    },
    image: {
      store: assets.assets.assets,
      list: results.imagesInUse,
      idKey: 'fileId',
      check: item => Boolean(item.usedInSigns[0]),
    },
    video: {
      store: assets.assets.assets,
      list: results.videosInUse,
      idKey: 'fileId',
      check: item => Boolean(item.usedInPlaylists[0]),
    },
    playlist: {
      store: playlists.playlists.playlists,
      list: results.playlistsInUse,
      idKey: 'content_id',
      check: item => Boolean(item.usedInSigns[0]),
    },
  };

  items.forEach(item => {
    let used = false;
    const config = typeMap[item.type];

    if (config) {
      const currItem = config.store.find(entry => +entry.id === +item[config.idKey]);

      if (currItem && config.check(currItem)) {
        config.list.push(currItem);
        used = true;
      }
    } else if (item.type === 'campaign') {
      const currItem = campaignSchedule.campaignSchedule.campaignScheduleEntries.find(
        entry => entry.campaignId === item.content_id,
      );

      if (currItem) {
        results.campaignsInUse.push(currItem);
        used = true;
      }
    }

    if (!used) {
      results.itemsToDelete.push(item);
    }
  });

  return results;
};

/**
 * Shows a warning dialog for delete operations that are blocked because items are still in use.
 * @param {Object} params - Warning dialog dependencies.
 * @param {DeletionFilterResult} params.items - Grouped deletion results with blocked items.
 * @param {{ current: { show: (title: string, body: JSX.Element) => void } }} params.refWarning - Imperative warning dialog ref.
 * @returns {void}
 */
export const showFailedDeleteWarningHelper = ({ items, refWarning }) => {
  const total =
    items.foldersInUse.length +
    items.imagesInUse.length +
    items.playlistsInUse.length +
    items.campaignsInUse.length +
    items.videosInUse.length;

  if (!total) {
    return;
  }

  /**
   * Counts the distinct signs referenced by an item's usage markers.
   * @param {string[]} arr - Sign usage markers in the format `signId-slot`.
   * @returns {number} Number of unique signs using the item.
   */
  const getSignUsageCount = arr => new Set(arr.map(sign => sign.split('-')[0])).size;
  const usageConfigs = [
    {
      data: items.foldersInUse,
      type: 'folder',
      link: i => `/folders/${i.id}`,
      usage: i => pluralize('sign', getSignUsageCount(i.usedInSigns), true),
    },
    {
      data: items.imagesInUse,
      type: 'image',
      link: i => `/assets/${i.id}`,
      usage: i => pluralize('sign', getSignUsageCount(i.usedInSigns), true),
    },
    {
      data: items.playlistsInUse,
      type: 'playlist',
      link: i => `/playlists/${i.id}`,
      usage: i => pluralize('sign', getSignUsageCount(i.usedInSigns), true),
    },
    {
      data: items.campaignsInUse,
      type: 'campaign',
      link: i => `/campaigns/${i.campaignId}`,
      usage: () => <Link to="/campaign-schedule">Campaign Schedule</Link>,
    },
    {
      data: items.videosInUse,
      type: 'video',
      link: i => `/assets/${i.id}`,
      usage: i => pluralize('playlist', i.usedInPlaylists.length, true),
    },
  ];
  /**
   * Renders a single list entry describing why an item could not be deleted.
   * @param {Object} params - Render configuration for a blocked item.
   * @param {ContentItem|{ campaignId: string|number, campaignName: string }} params.item - The blocked item.
   * @param {string} params.type - Display label for the item type.
   * @param {(item: any) => string} params.link - Builds the destination URL for the item.
   * @param {(item: any) => React.ReactNode} params.usage - Builds the usage description shown after the item label.
   * @returns {JSX.Element} A warning list item for the blocked content entry.
   */
  const renderUsageItem = ({ item, type, link, usage }) => (
    <li key={item.id || item.campaignId}>
      <Link to={link(item)} target="_blank" rel="noopener noreferrer">
        {item.name || item.campaignName}
      </Link>
      <span className="text-light"> [{type}]</span> used in {usage(item)}
    </li>
  );
  const body = (
    <div>
      <p>
        {pluralize('items', items.itemsToDelete.length, true)}{' '}
        {pluralize('is', items.itemsToDelete.length)} deleted.
      </p>

      <Alert bsStyle="warning">
        {pluralize('item', total, true)} cannot be deleted because the {pluralize('item', total)}{' '}
        {pluralize('is', total)} in use:
      </Alert>

      <ul>
        {usageConfigs.map(config =>
          config.data.map(item =>
            renderUsageItem({
              item,
              type: config.type,
              link: config.link,
              usage: config.usage,
            }),
          ),
        )}
      </ul>
    </div>
  );

  refWarning.current.show('Delete Warning', body);
};

/**
 * Merges sequence and playlist entry data into content items and filters out disallowed Lite-tier types.
 * @param {Object} params - Data required to enrich the item list.
 * @param {ContentItem[]} params.items - Items currently being displayed.
 * @param {Array<{ id: string|number, sequenceEntries: Array<object> }>} params.sequences - Available sequence records.
 * @param {Array<{ id: string|number, playlistEntries: Array<object> }>} params.playlists - Available playlist records.
 * @param {{ atLeastTierPro: boolean }} params.permissions - Permission flags used for Lite-tier filtering.
 * @returns {ContentItem[]} The merged and permission-filtered item list.
 */
export const getMergedItems = ({ items, sequences, playlists, permissions }) => {
  if (!sequences || !playlists) {
    return items;
  }

  const mergeMap = {
    sequence: { source: sequences, key: 'sequenceEntries' },
    playlist: { source: playlists, key: 'playlistEntries' },
  };
  const mergedItems = items.map(item => {
    const mapData = mergeMap[item.type];

    if (mapData) {
      const foundItem = mapData.source.find(el => +el.id === +item.content_id);

      if (foundItem) {
        return {
          ...item,
          [mapData.key]: foundItem[mapData.key],
        };
      }
    }

    return item;
  });

  return mergedItems.filter(item => {
    // exclude some items from Lite tier
    return !(
      !permissions.atLeastTierPro && ['folder', 'image', 'sign', 'campaign'].includes(item.type)
    );
  });
};

/**
 * Extracts the folder paths implied by uploaded files with relative paths.
 * @param {Array<File & { webkitRelativePath?: string }>} files - Uploaded files to inspect.
 * @returns {string[]} Unique folder paths that should be created before uploading the files.
 */
export const getUploadingFolders = files => {
  const folders = [];

  files
    .filter(item => item.webkitRelativePath)
    .forEach(item => {
      const parents = item.webkitRelativePath.slice(0, -item.name.length - 1).split('/');
      let fullPath = '';

      parents.forEach(parent => {
        fullPath += `${parent}/`;

        if (!folders.includes(fullPath)) {
          folders.push(fullPath);
        }
      });
    });

  return folders;
};

/**
 * Finds uploaded root folders that would collide with existing folders under the target parent.
 * @param {Object} params - Folder comparison inputs.
 * @param {string[]} params.uploadingFolders - Folder paths derived from the uploaded files.
 * @param {string[]} params.rootFolders - Mutable accumulator for uploaded root folder names.
 * @param {Array<{ name: string, parent: string }>} params.folders - Existing folders available under the current parent.
 * @param {string} params.parentPath - Parent path where the upload is targeted.
 * @returns {string[]} Folder names that already exist at the target parent path.
 */
export const getConflictFolderName = ({ uploadingFolders, rootFolders, folders, parentPath }) => {
  uploadingFolders.forEach(item => {
    const folder = item.split('/')[0];

    if (!rootFolders.includes(folder)) {
      rootFolders.push(folder);
    }
  });

  return folders
    .filter(item => item.parent === parentPath && rootFolders.includes(item.name))
    .map(item => item.name);
};

/**
 * Splits a relative folder path into a folder name and normalized parent path.
 * @param {string} pathString - Relative folder path to parse.
 * @param {string} basePath - Absolute parent path that the relative path should be appended to.
 * @returns {{ name: string|undefined, parent: string }} Parsed folder name and normalized parent path.
 */
export const parsePath = (pathString, basePath) => {
  const segments = pathString.split('/').filter(Boolean);
  const name = segments.pop();
  const parent = `${basePath}${segments.join('/')}`.replace(/\/?$/, '/');

  return { name, parent };
};

/**
 * Creates any missing upload folders one at a time before starting the file upload flow.
 * @param {Object} props - Folder creation and upload dependencies.
 * @param {string[]} props.items - Remaining folder paths that still need to be created.
 * @param {(query: string) => *} props.loadFolders - Reloads folders when folder creation is complete.
 * @param {(query: string) => *} props.loadContentItems - Reloads visible content when folder creation is complete.
 * @param {string} props.search - Current content query string.
 * @param {(files: Array<File>) => *} props.uploadStart - Starts uploading once folders are ready.
 * @param {Array<File>} props.files - Files waiting to be uploaded.
 * @param {string} props.parentPath - Destination parent path for newly created folders.
 * @param {(folder: { name: string|undefined, parent: string }) => Promise<*>} props.addFolder - Creates a folder in the back end.
 * @param {(items: string[], files: Array<File>) => *} props.addUploadingFolders - Continues creating the remaining folders.
 * @param {(notification: { type: string, text: string }) => void} props.addNotification - Displays folder creation feedback.
 * @param {(error: any) => string} props.errorMessage - Formats API errors for notifications.
 * @returns {Promise<void>} Resolves after the next folder is handled or uploads are started.
 */
export const createFolderForItems = async props => {
  const {
    items,
    loadFolders,
    loadContentItems,
    search,
    uploadStart,
    files,
    parentPath,
    addFolder,
    addUploadingFolders,
    addNotification,
    errorMessage,
  } = props;

  if (!items.length) {
    loadFolders('?count=100000000');
    loadContentItems(search);

    return uploadStart(files);
  }

  const item = items.shift();
  const { name, parent } = parsePath(item, parentPath);

  try {
    await addFolder({ name, parent });
    addUploadingFolders(items, files);
    addNotification({
      type: 'success',
      text: `Folder created: ${parent}${name}`,
    });
  } catch (err) {
    addNotification({
      type: 'danger',
      text: errorMessage(err),
    });
  }
};

/**
 * Filters the currently displayed items down to the selected folders.
 * @param {ContentItem[]} data - Items currently shown to the user.
 * @param {SelectedContentMap|undefined|null} selected - Selected item map keyed by row index.
 * @returns {ContentItem[]} Selected items whose type is `folder`.
 */
export const getSelectedFolders = (data, selected) => {
  if (!selected) {
    return [];
  }

  return data.filter((contentItem, index) => selected[index] && contentItem.type === 'folder');
};

/**
 * Calculates the next selection state for table interactions, including shift and modifier-key selection.
 * @param {Object} params - Selection calculation inputs.
 * @param {{ shiftKey: boolean, ctrlKey: boolean, metaKey: boolean }} params.event - Modifier state captured from the click event.
 * @param {number} params.index - Index of the clicked row.
 * @param {ContentItem} params.content - Content item associated with the clicked row.
 * @param {SelectedContentMap} params.selectedContent - Current selection map.
 * @param {number|false} params.shiftLastIndex - Most recent selection index used for shift selection.
 * @param {ContentItem[]} params.itemsToDisplay - Currently displayed items used for range selection.
 * @returns {{ newSelection: SelectedContentMap, newShiftIndex: number|false }} Updated selection map and shift anchor index.
 */
export const calculateNewSelection = ({
  event,
  index,
  content,
  selectedContent,
  shiftLastIndex,
  itemsToDisplay,
}) => {
  let newSelection = { ...selectedContent };
  let newShiftIndex = shiftLastIndex;

  if (event.shiftKey && shiftLastIndex !== false) {
    const start = Math.min(shiftLastIndex, index);
    const end = Math.max(shiftLastIndex, index);

    for (let pos = start; pos <= end; pos += 1) {
      newSelection[pos] = { ...itemsToDisplay[pos], index: pos };
    }

    return { newSelection, newShiftIndex: shiftLastIndex };
  }

  const isMac =
    window !== undefined && navigator !== undefined && /Mac/.test(navigator.userAgent || '');
  const modifierKey = event[isMac ? 'metaKey' : 'ctrlKey'];

  if (modifierKey) {
    if (newSelection[index]) {
      delete newSelection[index];
      newShiftIndex = false;
    } else {
      newSelection[index] = { ...content, index };
      newShiftIndex = index;
    }

    return { newSelection, newShiftIndex };
  }

  if (!newSelection[index]) {
    newSelection = { [index]: { ...content, index } };
    newShiftIndex = index;
  }

  return { newSelection, newShiftIndex };
};

/**
 * Builds and executes the correct move request for a single content item.
 * @param {ContentItem} item - Item being moved.
 * @param {{ id: string|number, name: string, parent: string }} parent - Destination folder metadata.
 * @param {Object} services - Service functions required to load and update each content type.
 * @param {(id: string) => Promise<{ result: object }>} services.getAsset - Loads an asset by file id.
 * @param {(id: string, payload: object) => Promise<*>} services.updateAsset - Updates an asset.
 * @param {(id: string|number) => Promise<{ result: object }>} services.loadPlaylist - Loads a playlist.
 * @param {(id: string|number, payload: object) => Promise<*>} services.updatePlaylist - Updates a playlist.
 * @param {(id: string|number) => Promise<{ result: object }>} services.getSign - Loads a sign.
 * @param {(id: string|number, payload: object) => Promise<*>} services.updateSign - Updates a sign.
 * @param {(id: string|number) => Promise<{ result: object }>} services.loadSequence - Loads a sequence.
 * @param {(id: string|number, payload: object) => Promise<*>} services.updateSequence - Updates a sequence.
 * @param {(id: string|number) => Promise<{ result: object }>} services.loadSchedule - Loads a schedule.
 * @param {(id: string|number, payload: object) => Promise<*>} services.updateSchedule - Updates a schedule.
 * @param {(id: string|number) => Promise<{ result: object }>} services.loadCampaign - Loads a campaign.
 * @param {(id: string|number, payload: object) => Promise<*>} services.updateCampaign - Updates a campaign.
 * @param {(id: string|number) => Promise<{ result: object }>} services.loadFolder - Loads a folder.
 * @param {(id: string|number, payload: object) => Promise<*>} services.updateFolder - Updates a folder.
 * @returns {Promise<*>} A promise that resolves when the move request completes.
 */
export const getMoveAction = (item, parent, services) => {
  const { id, name, parent: parentPath } = parent;
  const { content_id, fileId, type } = item;
  const targetId = ['image', 'video', 'file'].includes(type) ? fileId : content_id;
  const assetActions = { get: services.getAsset, update: services.updateAsset };
  const mappings = {
    image: assetActions,
    video: assetActions,
    file: assetActions,
    playlist: { get: services.loadPlaylist, update: services.updatePlaylist },
    sign: { get: services.getSign, update: services.updateSign },
    sequence: { get: services.loadSequence, update: services.updateSequence },
    schedule: { get: services.loadSchedule, update: services.updateSchedule },
    campaign: { get: services.loadCampaign, update: services.updateCampaign },
    folder: { get: services.loadFolder, update: services.updateFolder },
  };
  const action = mappings[type];

  if (!action) {
    return Promise.reject(new Error('Unknown action type'));
  }

  const newParent = type === 'folder' ? `${parentPath}${name}/` : id;

  return action
    .get(targetId)
    .then(res => action.update(targetId, { ...res.result, parent: newParent }));
};

/**
 * Renders the folder-specific delete confirmation content with the deep-delete toggle.
 * @param {(value: boolean) => void} setDeepDelete - Updates the deep-delete checkbox state.
 * @param {boolean} deepDelete - Current deep-delete checkbox value.
 * @returns {JSX.Element} Additional modal content for folder deletion warnings and controls.
 */
export const getDeleteFolderComponent = (setDeepDelete, deepDelete) => (
  <>
    <div className="mx-auto mt-2 max-w-full mb-2">
      <div className="group relative flex items-center space-x-3 rounded-xl border border-orange-100 bg-orange-50/50 p-2 transition-all duration-300 hover:shadow-sm">
        <div className="mt-0.5 flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100/80 text-orange-600">
            <i className="text-[20px] !text-orange-600 material-icons ">info_outline</i>
          </div>
        </div>

        <div className="flex-1">
          <p className="mt-1 text-sm leading-relaxed text-orange-700/80 !mb-0">
            Note that the items in the deleted folder will be moved into the folder above.
            <br />
            <span className="font-medium text-orange-900 underline decoration-orange-200 underline-offset-2">
              If you wish to delete the contents of this folder, tick the checkbox below.
            </span>{' '}
          </p>
        </div>
      </div>
    </div>

    <div className="mt-2 flex items-center text-center poppins-bold">
      <input
        id="delete-all-folder"
        className="!mr-4 !mt-0"
        type="checkbox"
        checked={deepDelete}
        onChange={e => {
          setDeepDelete(e.target.checked);
        }}
      />
      <label htmlFor="delete-all-folder" className="!mb-0 cursor-pointer text-dark-night">
        Delete contents of this folder
      </label>
    </div>
  </>
);
